import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Loader2, ShieldCheck, ArrowRight, Check, Mail, User, Lock, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAuthData } = useAuth();

  const [tiers, setTiers] = useState([]);
  const [selectedTierKey, setSelectedTierKey] = useState(searchParams.get('tier') || 'client_pro_month');
  const [loadingTiers, setLoadingTiers] = useState(true);

  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/subscriptions/tiers`);
        // Solo tiers de cliente B2C
        const clientTiers = (r.data?.tiers || []).filter(
          (t) => t.scope === 'client' || t.scope === 'free'
        );
        setTiers(clientTiers);
      } catch (e) {
        toast.error('No se pudieron cargar los planes');
      } finally {
        setLoadingTiers(false);
      }
    })();
  }, []);

  const selectedTier = tiers.find((t) => t.key === selectedTierKey);

  const isValid =
    form.name.trim().length >= 2 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) &&
    form.password.length >= 6 &&
    form.password === form.confirmPassword &&
    acceptTerms &&
    selectedTierKey;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) {
      if (form.password !== form.confirmPassword) {
        toast.error('Las contraseñas no coinciden');
      } else if (form.password.length < 6) {
        toast.error('Contraseña mínima de 6 caracteres');
      } else if (!acceptTerms) {
        toast.error('Acepta los términos para continuar');
      }
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/auth/register-and-checkout`, {
        email: form.email.trim().toLowerCase(),
        name: form.name.trim(),
        password: form.password,
        tier_key: selectedTierKey,
        origin_url: window.location.origin,
        accept_terms: true,
        origin_tag: searchParams.get('origen') || null,
      });

      // Guardar token y user
      if (r.data?.token && setAuthData) {
        setAuthData({ token: r.data.token, user: r.data.user });
      } else if (r.data?.token) {
        localStorage.setItem('token', r.data.token);
        localStorage.setItem('user', JSON.stringify(r.data.user));
        axios.defaults.headers.common['Authorization'] = `Bearer ${r.data.token}`;
      }

      // Si hay checkout URL -> redirigir a Stripe
      if (r.data?.checkout_url) {
        toast.success('Cuenta creada. Redirigiendo al pago...');
        setTimeout(() => {
          window.location.href = r.data.checkout_url;
        }, 800);
      } else {
        // Free → directo al portal
        toast.success('¡Bienvenido! Cuenta creada con plan gratuito');
        setTimeout(() => navigate('/portal'), 600);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error al registrarte';
      toast.error(detail);
      setSubmitting(false);
    }
  };

  const formatPrice = (t) => {
    if (!t || t.amount === 0) return 'Gratis';
    return `${t.amount.toFixed(2)} €/${t.period === 'year' ? 'año' : 'mes'}`;
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_plan-tracker-25/artifacts/7nvt53w6_Sin%20t%C3%ADtulo.jpeg"
            alt="GuardiansFit"
            className="h-20 mx-auto mb-4"
          />
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground">Crea tu cuenta</h1>
          <p className="text-muted-foreground mt-2 text-sm sm:text-base">
            Empieza tu plan en 1 minuto. 14 días gratis en Pro.
          </p>
        </div>

        {/* Step 1: Elegir plan */}
        <Card className="bg-card border-border p-4 sm:p-6 mb-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-7 w-7 rounded-full bg-primary text-zinc-900 flex items-center justify-center font-bold text-sm">
              1
            </div>
            <h2 className="text-lg font-bold">Elige tu plan</h2>
          </div>

          {loadingTiers ? (
            <div className="text-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto" />
            </div>
          ) : (
            <div className="grid sm:grid-cols-3 gap-3">
              {tiers.map((t) => {
                const isSelected = selectedTierKey === t.key;
                const isPopular = t.key === 'client_pro_month';
                return (
                  <button
                    type="button"
                    key={t.key}
                    onClick={() => setSelectedTierKey(t.key)}
                    data-testid={`tier-${t.key}`}
                    className={`text-left rounded-xl border-2 transition-all p-4 relative ${
                      isSelected
                        ? 'border-primary bg-primary/10 shadow-lg shadow-primary/20'
                        : 'border-zinc-700 bg-zinc-900/50 hover:border-zinc-500'
                    }`}
                  >
                    {isPopular && (
                      <span className="absolute -top-2 right-3 text-[10px] font-bold bg-primary text-zinc-900 px-2 py-0.5 rounded-full">
                        POPULAR
                      </span>
                    )}
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-bold text-sm">{t.name}</h3>
                      {isSelected && <Check className="h-4 w-4 text-primary" />}
                    </div>
                    <p className="text-2xl font-bold text-primary mb-1">{formatPrice(t)}</p>
                    {t.trial_days > 0 && t.amount > 0 && (
                      <p className="text-[11px] text-emerald-400 mb-2">
                        {t.trial_days} días gratis
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground leading-snug">{t.description}</p>
                  </button>
                );
              })}
            </div>
          )}
        </Card>

        {/* Step 2: Datos */}
        <Card className="bg-card border-border p-4 sm:p-6 mb-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-7 w-7 rounded-full bg-primary text-zinc-900 flex items-center justify-center font-bold text-sm">
              2
            </div>
            <h2 className="text-lg font-bold">Tus datos</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <Label htmlFor="reg-name">Nombre</Label>
              <div className="relative">
                <User className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  id="reg-name"
                  data-testid="reg-name-input"
                  type="text"
                  className="pl-10"
                  placeholder="Tu nombre"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="reg-email">Email</Label>
              <div className="relative">
                <Mail className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  id="reg-email"
                  data-testid="reg-email-input"
                  type="email"
                  className="pl-10"
                  placeholder="tu@email.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="reg-pass">Contraseña</Label>
                <div className="relative">
                  <Lock className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    id="reg-pass"
                    data-testid="reg-pass-input"
                    type="password"
                    className="pl-10"
                    placeholder="Mínimo 6 caracteres"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="reg-pass2">Repite contraseña</Label>
                <div className="relative">
                  <Lock className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    id="reg-pass2"
                    data-testid="reg-pass2-input"
                    type="password"
                    className="pl-10"
                    placeholder="Repite"
                    value={form.confirmPassword}
                    onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                    required
                  />
                </div>
                {form.confirmPassword && form.password !== form.confirmPassword && (
                  <p className="text-[11px] text-red-400 mt-1">No coinciden</p>
                )}
              </div>
            </div>

            <div className="flex items-start gap-2 pt-2">
              <Checkbox
                id="reg-terms"
                data-testid="reg-terms-checkbox"
                checked={acceptTerms}
                onCheckedChange={setAcceptTerms}
              />
              <label htmlFor="reg-terms" className="text-xs text-muted-foreground leading-snug cursor-pointer">
                Acepto los{' '}
                <Link to="/legal/terminos" className="text-primary hover:underline" target="_blank">
                  términos de uso
                </Link>{' '}
                y la{' '}
                <Link to="/legal/privacidad" className="text-primary hover:underline" target="_blank">
                  política de privacidad
                </Link>
                .
              </label>
            </div>

            {/* Resumen + CTA */}
            <div className="bg-primary/10 border border-primary/30 rounded-lg p-3 mt-3 flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Plan seleccionado</p>
                <p className="font-bold text-foreground">
                  {selectedTier?.name || '...'} · {formatPrice(selectedTier)}
                </p>
                {selectedTier?.trial_days > 0 && selectedTier?.amount > 0 && (
                  <p className="text-[11px] text-emerald-400">
                    {selectedTier.trial_days} días gratis · cancela cuando quieras
                  </p>
                )}
              </div>
              <Sparkles className="h-6 w-6 text-primary" />
            </div>

            <Button
              type="submit"
              disabled={!isValid || submitting}
              data-testid="reg-submit-btn"
              className="w-full bg-primary hover:bg-primary/90 text-zinc-900 font-bold py-3 text-base"
            >
              {submitting ? (
                <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Procesando...</>
              ) : selectedTier?.amount > 0 ? (
                <>Continuar al pago seguro <ArrowRight className="h-5 w-5 ml-2" /></>
              ) : (
                <>Crear cuenta gratuita <ArrowRight className="h-5 w-5 ml-2" /></>
              )}
            </Button>

            <p className="text-[11px] text-muted-foreground text-center pt-2 flex items-center justify-center gap-1">
              <ShieldCheck className="h-3 w-3 text-emerald-400" />
              Pago seguro vía Stripe · Tus datos nunca pasan por nuestros servidores
            </p>
          </form>
        </Card>

        {/* Footer */}
        <p className="text-center text-sm text-muted-foreground">
          ¿Ya tienes cuenta?{' '}
          <Link to="/login" className="text-primary font-semibold hover:underline">
            Inicia sesión
          </Link>
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
